/**
 * Hover card on the Deliverables rail title: a small flowchart (mermaid "graph LR"
 * look, drawn as SVG) of how the whole-project tracks were split into structure
 * groups — when, by whom, and which tracks continued under each group.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { GitFork, Layers } from 'lucide-react';
import type { ProjectFileStream } from '../../types';
import type { StructureRef } from '../../utils/proposalStructureRefs';
import { useCRM } from '../../utils/CRMContext';
import {
  buildFilesStructureHistory,
  type FilesStructureHistoryEvent,
  type SplitHistoryEvent,
  type StructureSetupEvent,
} from '../../utils/projectFileSplitHistory';
import { trackKindShort } from './projectUploadModalShared';
import './SplitHistoryPopover.css';

export interface SplitHistoryPopoverProps {
  streams: ProjectFileStream[];
  structures: StructureRef[];
  /** Versions per track id. */
  versionCounts: ReadonlyMap<string, number>;
  /** The rail title; the whole thing becomes the hover target. */
  children: React.ReactNode;
}

// ── Diagram geometry ──────────────────────────────────────────────────────────
const PAD = 10;
const ROOT_W = 236;
const GROUP_W = 268;
const GAP_X = 64;
const HEADING_H = 15;
const GAP_Y = 12;
const ROW_H = 15;
const TITLE_H = 17;
const SUB_H = 13;
const NODE_PAD = 8;

function trunc(s: string, max: number): string {
  const t = (s || '').trim();
  return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t;
}

export function fmtSplitWhen(d: Date): string {
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}
const fmtWhen = fmtSplitWhen;

interface NodeSpec {
  x: number;
  y: number;
  w: number;
  h: number;
  title: string;
  titleHint?: string;
  sub?: string;
  rows: DiagramRow[];
  tone: 'root' | 'group';
}

/** A track row, or a small heading that separates row groups inside one node. */
type DiagramRow = { kind: string; text: string; muted?: string; heading?: undefined } | { heading: string; kind?: undefined };

function rowsHeight(rows: DiagramRow[]): number {
  return rows.reduce((h, r) => h + (r.heading !== undefined ? HEADING_H : ROW_H), 0);
}
function nodeHeight(rows: DiagramRow[], sub: boolean): number {
  return NODE_PAD + TITLE_H + (sub ? SUB_H : 0) + rowsHeight(rows) + NODE_PAD;
}

const renderDiagramNode = (n: NodeSpec, key: string) => (
  <g key={key} className={`pf-splitdiag-node pf-splitdiag-node--${n.tone}`} transform={`translate(${n.x} ${n.y})`}>
    <rect width={n.w} height={n.h} rx={7} />
    <text className="pf-splitdiag-title" x={NODE_PAD + 2} y={NODE_PAD + 12}>
      {n.titleHint ? <title>{n.titleHint}</title> : null}
      {n.title}
    </text>
    {n.sub ? (
      <text className="pf-splitdiag-sub" x={NODE_PAD + 2} y={NODE_PAD + TITLE_H + 9}>
        {n.sub}
      </text>
    ) : null}
    {(() => {
      let y = NODE_PAD + TITLE_H + (n.sub ? SUB_H : 0);
      return n.rows.map((r, i) => {
        if (r.heading !== undefined) {
          const hy = y + 11;
          y += HEADING_H;
          return (
            <text key={i} x={NODE_PAD + 2} y={hy} className="pf-splitdiag-heading">
              {r.heading}
            </text>
          );
        }
        const ry = y + 11;
        y += ROW_H;
        const badgeW = r.kind.length * 6.2 + 8;
        return (
          <g key={i} className="pf-splitdiag-row">
            <rect x={NODE_PAD + 2} y={ry - 9} width={badgeW} height={12} rx={3} className={`pf-splitdiag-badge pf-splitdiag-badge--${r.kind.toLowerCase()}`} />
            <text x={NODE_PAD + 2 + badgeW / 2} y={ry} textAnchor="middle" className="pf-splitdiag-badge-text">
              {r.kind}
            </text>
            <text x={NODE_PAD + 2 + badgeW + 6} y={ry} className="pf-splitdiag-rowtext">
              {r.text}
            </text>
            {r.muted ? (
              <text x={n.w - NODE_PAD - 2} y={ry} textAnchor="end" className="pf-splitdiag-muted">
                {r.muted}
              </text>
            ) : null}
          </g>
        );
      });
    })()}
  </g>
);

/** Cubic "graph LR" edge from the right-middle of one node to the left-middle of another. */
const edgePath = (from: NodeSpec, to: NodeSpec): string => {
  const x1 = from.x + from.w;
  const y1 = from.y + from.h / 2;
  const x2 = to.x;
  const y2 = to.y + to.h / 2;
  const cx = (x1 + x2) / 2;
  return `M ${x1} ${y1} C ${cx} ${y1}, ${cx} ${y2}, ${x2 - 1} ${y2}`;
};

// ── One timeline: whole project → structures ─────────────────────────────────

interface MapStructure {
  key: string;
  names: string[];
  /** Tracks now living under this structure, in the order they got there. */
  tracks: { stream: ProjectFileStream; how: 'created' | 'filled' | 'tagged' | 'split' | 'added' }[];
}
interface MapStep {
  at: Date;
  byUid: string | null;
  byName: string | null;
  icon: 'layers' | 'fork';
  /** A group added after the tracks were already split, not a new split. */
  added?: boolean;
  text: string;
}
interface ProjectStructureMap {
  /** Whole-project tracks uploaded first (the 2D / 3D / HTML set). */
  rootTracks: ProjectFileStream[];
  /** Timber-list CSVs pushed afterwards, which set up the structures. */
  timberLists: { fileName: string; structureNames: string[] }[];
  timberListsAt: Date | null;
  structures: MapStructure[];
  steps: MapStep[];
}

const listNames = (names: string[], max = 3): string =>
  names.length <= max ? names.join(', ') : `${names.slice(0, max).join(', ')} +${names.length - max}`;

/** Folds every split / set-up / tag event into one 1 → many picture plus an ordered step list. */
function buildProjectStructureMap(events: FilesStructureHistoryEvent[], structureOrder: StructureRef[]): ProjectStructureMap {
  const rank = new Map(structureOrder.map((s, i) => [s.name, i]));
  const roots = new Map<string, ProjectFileStream>();
  const timberLists: ProjectStructureMap['timberLists'] = [];
  let timberListsAt: Date | null = null;
  const structs = new Map<string, MapStructure>();
  const steps: MapStep[] = [];

  const structFor = (names: string[]): MapStructure => {
    const key = names.join('|') || '—';
    let st = structs.get(key);
    if (!st) {
      st = { key, names, tracks: [] };
      structs.set(key, st);
    }
    return st;
  };
  const addTrack = (st: MapStructure, stream: ProjectFileStream, how: MapStructure['tracks'][number]['how']) => {
    if (!st.tracks.some(t => t.stream.id === stream.id)) st.tracks.push({ stream, how });
  };
  const addSplit = (ev: SplitHistoryEvent) => {
    for (const r of ev.roots) if (r.id) roots.set(r.id, r);
    for (const g of ev.groups) {
      const st = structFor(g.structureNames);
      for (const s of g.streams) addTrack(st, s, ev.addedAfterSplit ? 'added' : 'split');
    }
    const groupList = listNames(ev.groups.map(g => g.label));
    steps.push({
      at: ev.at,
      byUid: ev.byUid,
      byName: null,
      icon: 'fork',
      added: ev.addedAfterSplit,
      text: ev.addedAfterSplit
        ? `Added ${ev.groups.length} group${ev.groups.length !== 1 ? 's' : ''} after the split: ${groupList}`
        : `Split ${ev.roots.length} whole-project track${ev.roots.length !== 1 ? 's' : ''} into ${ev.groups.length} group${
            ev.groups.length !== 1 ? 's' : ''
          }: ${groupList}`,
    });
  };
  const addSetup = (ev: StructureSetupEvent) => {
    const csv = ev.entries.filter(e => e.stream.streamKind === 'timber_list_csv');
    const tagged = ev.entries.filter(e => e.stream.streamKind !== 'timber_list_csv');
    for (const e of csv) {
      if (e.fileName) timberLists.push({ fileName: e.fileName, structureNames: e.structureNames });
      addTrack(structFor(e.structureNames), e.stream, e.createdStructure ? 'created' : 'filled');
    }
    if (csv.length && (!timberListsAt || ev.at < timberListsAt)) timberListsAt = ev.at;
    for (const e of tagged) {
      if (e.stream.id) roots.set(e.stream.id, e.stream);
      addTrack(structFor(e.structureNames), e.stream, 'tagged');
    }
    if (csv.length) {
      const created = csv.filter(e => e.createdStructure).length;
      const names = Array.from(new Set(csv.flatMap(e => e.structureNames)));
      steps.push({
        at: ev.at,
        byUid: ev.byUid,
        byName: ev.byName,
        icon: 'layers',
        text: `${created > 0 ? `Created ${created} structure${created !== 1 ? 's' : ''}` : `Filled ${names.length} structure${names.length !== 1 ? 's' : ''}`} from ${
          csv.length
        } timber list${csv.length !== 1 ? 's' : ''}: ${listNames(names)}`,
      });
    }
    if (tagged.length) {
      const targets = Array.from(new Set(tagged.map(e => e.structureNames.join(' + '))));
      steps.push({
        at: ev.at,
        byUid: ev.byUid,
        byName: ev.byName,
        icon: 'layers',
        text: `Tagged ${listNames(tagged.map(e => trackKindShort(e.stream.streamKind)), 4)} track${tagged.length !== 1 ? 's' : ''} to ${listNames(targets)}`,
      });
    }
  };
  for (const ev of events) {
    if (ev.kind === 'split') addSplit(ev);
    else if (ev.kind === 'structures') addSetup(ev);
    else {
      addSetup(ev.setup);
      addSplit(ev.split);
    }
  }
  const structures = Array.from(structs.values()).sort((a, b) => {
    const ra = Math.min(...a.names.map(n => rank.get(n) ?? Number.MAX_SAFE_INTEGER));
    const rb = Math.min(...b.names.map(n => rank.get(n) ?? Number.MAX_SAFE_INTEGER));
    return ra - rb;
  });
  steps.sort((a, b) => a.at.getTime() - b.at.getTime());
  return { rootTracks: Array.from(roots.values()), timberLists, timberListsAt, structures, steps };
}

/** The map as a graph-LR: one "Whole project" node fanning out to every structure. */
const ProjectStructureDiagram: React.FC<{ map: ProjectStructureMap; versionCounts: ReadonlyMap<string, number> }> = ({
  map,
  versionCounts,
}) => {
  const versionsOf = (s: ProjectFileStream) => versionCounts.get(s.id ?? '') ?? 0;
  const rootRows: DiagramRow[] = map.rootTracks.map(r => ({
    kind: trackKindShort(r.streamKind),
    text: trunc(r.title, 22),
    muted: `${versionsOf(r)} v`,
  }));
  if (map.timberLists.length) {
    const when = map.timberListsAt ? map.timberListsAt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
    rootRows.push({ heading: `Then: timber lists${when ? ` · ${when}` : ''}` });
    for (const t of map.timberLists) rootRows.push({ kind: 'Timber', text: trunc(t.fileName, 28) });
  }
  const root: NodeSpec = {
    x: PAD,
    y: 0,
    w: ROOT_W,
    h: nodeHeight(rootRows, true),
    title: 'Whole project',
    sub: map.rootTracks.length ? 'uploaded first · one set of files' : 'one set of files',
    rows: rootRows,
    tone: 'root',
  };
  const groups: NodeSpec[] = [];
  let y = PAD;
  for (const st of map.structures) {
    const rows: DiagramRow[] = st.tracks.map(t => ({
      kind: trackKindShort(t.stream.streamKind),
      text: trunc(t.stream.title, 24),
      muted: `${versionsOf(t.stream)} v`,
    }));
    const hows = new Set(st.tracks.map(t => t.how));
    const sub =
      st.tracks.length > 0 && st.tracks.every(t => t.how === 'added')
        ? 'added after the split'
        : hows.has('created')
          ? 'new on the proposal · set up from its timber list'
          : hows.has('filled')
            ? 'filled from its timber list'
            : hows.has('split')
              ? 'split from the whole project'
              : 'tagged from the whole project';
    const h = nodeHeight(rows, true);
    groups.push({
      x: PAD + ROOT_W + GAP_X,
      y,
      w: GROUP_W,
      h,
      title: trunc(st.names.join(' + '), 34),
      titleHint: st.names.join(', '),
      sub,
      rows,
      tone: 'group',
    });
    y += h + GAP_Y;
  }
  const stackH = Math.max(y - GAP_Y + PAD, root.h + PAD * 2);
  root.y = Math.max(PAD, (stackH - root.h) / 2);
  const width = PAD + ROOT_W + GAP_X + GROUP_W + PAD;
  return (
    <svg className="pf-splitdiag" width={width} height={stackH} viewBox={`0 0 ${width} ${stackH}`} role="img" aria-label="Whole project to structures map">
      <defs>
        <marker id="pf-splitdiag-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" />
        </marker>
      </defs>
      {groups.map((g, i) => (
        <path key={`e${i}`} className="pf-splitdiag-edge" d={edgePath(root, g)} markerEnd="url(#pf-splitdiag-arrow)" />
      ))}
      {renderDiagramNode(root, 'root')}
      {groups.map((g, i) => renderDiagramNode(g, `g${i}`))}
    </svg>
  );
};

const SplitHistoryPopover: React.FC<SplitHistoryPopoverProps> = ({ streams, structures, versionCounts, children }) => {
  const { crmUsers } = useCRM();
  const events = useMemo(() => buildFilesStructureHistory(streams, structures), [streams, structures]);
  const map = useMemo(() => buildProjectStructureMap(events, structures), [events, structures]);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | null>(null);

  const nameOf = useCallback(
    (uid: string | null) => (uid ? crmUsers.find(u => u.uid === uid)?.displayName || null : null),
    [crmUsers],
  );

  const show = useCallback(() => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    const r = wrapRef.current?.getBoundingClientRect();
    if (r) {
      const width = Math.min(640, window.innerWidth - 16);
      setPos({ top: r.bottom + 6, left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)) });
    }
    setOpen(true);
  }, []);
  const hide = useCallback(() => {
    closeTimer.current = window.setTimeout(() => setOpen(false), 120);
  }, []);
  useEffect(() => () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
  }, []);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (events.length === 0 || map.structures.length === 0) return <>{children}</>;

  const n = map.structures.length;
  const first = map.steps[0];
  const firstWho = first ? nameOf(first.byUid) || first.byName : null;
  return (
    <div
      ref={wrapRef}
      className="pf-split-history"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      <button
        type="button"
        className="pf-split-history-glyph"
        aria-label="Show how this project's files were organised into structures"
        aria-expanded={open}
        onClick={() => (open ? setOpen(false) : show())}
      >
        <GitFork size={11} aria-hidden />
        {n}
      </button>
      {open && pos
        ? createPortal(
            <div
              className="pf-split-history-card"
              role="dialog"
              aria-label="How this project was organised into structures"
              style={{ top: pos.top, left: pos.left }}
              onMouseEnter={show}
              onMouseLeave={hide}
            >
              <section className="pf-split-history-event">
                <header className="pf-split-history-head">
                  <Layers size={13} aria-hidden />
                  <span>
                    <strong>
                      One project → {n} structure{n !== 1 ? 's' : ''}
                    </strong>
                    {first ? ` · started ${fmtWhen(first.at)}` : ''}
                    {firstWho ? ` · ${firstWho}` : ''}
                  </span>
                </header>
                <ProjectStructureDiagram map={map} versionCounts={versionCounts} />
                <ol className="pf-split-history-steps">
                  {map.steps.map((st, i) => {
                    const who = nameOf(st.byUid) || st.byName;
                    return (
                      <li key={i} className="pf-split-history-step">
                        {st.icon === 'fork' ? <GitFork size={11} aria-hidden /> : <Layers size={11} aria-hidden />}
                        <span>
                          <strong>{st.text}</strong>
                          <span className="pf-split-history-step-meta">
                            {' '}
                            · {fmtWhen(st.at)}
                            {who ? ` · ${who}` : ''}
                          </span>
                        </span>
                      </li>
                    );
                  })}
                </ol>
                <p className="pf-split-history-note">
                  {map.steps.some(st => st.added)
                    ? 'A group added after the split was not part of that split. Its tracks start the day it was added, and earlier whole-project files stay with the groups that already had them.'
                    : 'The whole-project files came in first; the timber lists then set up each structure (timber lines and board feet applied) and the shared tracks were tagged or split under them. Same versions, nothing copied — numbering carries on under each structure.'}
                </p>
              </section>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
};

export default SplitHistoryPopover;
